<template>
  <div data-app>
    <div
      role="alert"
      v-bind:class="{ show: errors.length }"
      class="alert fade alert-danger"
    >
      <div class="alert-text" v-for="(error, i) in errors" :key="i">
        {{ error }}
      </div>
    </div>

    <v-card class="mb-5">
      <v-card-title>Signaling Sessions</v-card-title>
      <v-card-subtitle>
        Signaling session configuration for this account. This is only required
        for on-demand customers who need to trigger different downstream routing
        behavior when a prefix is diverted.
      </v-card-subtitle>
      <v-card-subtitle v-if="missingConfig" class="pt-0">
        No signaling session is configured for this account. Please reach out to
        support for assistance in setting one up.
      </v-card-subtitle>
      <v-card-text v-if="!missingConfig">
        <v-row dense>
          <v-col cols="12" sm="6" md="3">
            <v-card flat>
              <v-card-title class="text-h6">
                ASN
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  <span>{{ fieldHints.asn }}</span>
                </v-tooltip>
              </v-card-title>
              <v-card-text class="text-body-2">
                {{ formatAsn(signalingConfig.asn) }}
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card flat>
              <v-card-title class="text-h6">
                Password
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  <span>{{ fieldHints.password }}</span>
                </v-tooltip>
              </v-card-title>
              <v-card-text class="text-body-2">
                {{ signalingConfig.password || "—" }}
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card flat>
              <v-card-title class="text-h6">
                Attack Community
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  <span>{{ fieldHints.attack_community }}</span>
                </v-tooltip>
              </v-card-title>
              <v-card-text class="text-body-2">
                {{ signalingConfig.attack_community ?? "—" }}
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card flat>
              <v-card-title class="text-h6">
                Next-Hop Override
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  <span>{{ fieldHints.set_nexthop_if_nexthop_self }}</span>
                </v-tooltip>
              </v-card-title>
              <v-card-text class="text-body-2">
                {{ signalingConfig.set_nexthop_if_nexthop_self || "—" }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div v-if="!missingConfig">
      <v-card>
        <v-card-title>Neighbors</v-card-title>
        <v-card-subtitle>Manage signaling neighbors.</v-card-subtitle>
        <v-data-table
          :loading="loading"
          :headers="neighborHeaders"
          :items="neighbors"
          :search="search"
          :footer-props="{
            itemsPerPageOptions: [5, 10, 50, 100, 250, -1],
            showFirstLastPage: false,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right',
            prevIcon: 'mdi-chevron-left',
            nextIcon: 'mdi-chevron-right',
          }"
          item-key="address"
          class="elevation-1"
        >
          <template v-slot:top>
            <v-toolbar flat color="white">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
              ></v-text-field>
              <v-spacer></v-spacer>
              <v-dialog v-model="dialog" max-width="500px">
                <template v-slot:activator="{ on }">
                  <v-btn color="primary" dark class="mb-2" v-on="on">
                    New Neighbor
                  </v-btn>
                </template>
                <v-card>
                  <v-card-title>
                    <span class="headline">New Neighbor</span>
                  </v-card-title>
                  <v-card-text>
                    <v-form ref="neighborForm" lazy-validation>
                      <v-container>
                        <v-row>
                          <v-col cols="12">
                            <v-text-field
                              v-model.trim="editedItem.address"
                              label="Address*"
                              :rules="addressRules"
                              required
                            />
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-form>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="blue darken-1" text @click="closeDialog">
                      Cancel
                    </v-btn>
                    <v-btn color="blue darken-1" text @click="saveNeighbor">
                      Save
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-toolbar>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon small @click="deleteNeighbor(item)">mdi-delete</v-icon>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import api from "@/common/api.service";
import Errors from "@/common/errors";
import ipAddr from "ip-address";

export default {
  mounted() {
    this.loadData();
  },
  data: () => ({
    errors: [],
    loading: true,
    missingConfig: false,
    signalingConfig: null,
    neighbors: [],
    search: "",
    dialog: false,
    editedItem: {
      address: "",
    },
    neighborHeaders: [
      { text: "Address", align: "left", value: "address" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    addressRules: [
      (v) => !!v || "Address is required",
      (v) =>
        (!!v &&
          (new ipAddr.Address4(v).isValid() ||
            new ipAddr.Address6(v).isValid())) ||
        "Address is invalid",
    ],
    fieldHints: {
      asn: "ASN used for both sides of the peering session",
      password: "BGP password",
      attack_community:
        "BGP community to add for under-attack prefixes that get announced back to neighbors",
      set_nexthop_if_nexthop_self:
        "Replace the next-hop for routes reflected back to neighbors if the received route's next-hop is the same as the neighbor's address",
    },
  }),
  watch: {
    dialog(val) {
      val || this.closeDialog();
    },
  },
  methods: {
    loadData() {
      this.loading = true;
      this.errors = [];

      api
        .query("/v2/signaling")
        .then((response) => {
          this.signalingConfig = response.data;
          this.missingConfig = false;
          return api.query("/v2/signaling/neighbors");
        })
        .then((response) => {
          this.neighbors = response.data.neighbors || [];
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            this.missingConfig = true;
            this.signalingConfig = null;
            this.neighbors = [];
            return;
          }
          this.errors.push(...Errors.extractErrorMessage(error));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    closeDialog() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = { address: "" };
      }, 300);
    },
    saveNeighbor() {
      if (!this.$refs.neighborForm.validate()) return;

      api
        .post("/v2/signaling/neighbors", {
          address: this.editedItem.address,
        })
        .then((response) => {
          this.neighbors.push(response.data);
          this.closeDialog();
        })
        .catch((error) =>
          this.errors.push(...Errors.extractErrorMessage(error))
        );
    },
    deleteNeighbor(item) {
      if (
        !confirm(`Are you sure you want to delete neighbor ${item.address}?`)
      ) {
        return;
      }

      api
        .delete(`/v2/signaling/neighbors/${item.address}`)
        .then(() => {
          const index = this.neighbors.indexOf(item);
          if (index > -1) this.neighbors.splice(index, 1);
        })
        .catch((error) =>
          this.errors.push(...Errors.extractErrorMessage(error))
        );
    },
    formatAsn(asn) {
      return asn ? `AS${asn}` : "—";
    },
  },
};
</script>
